function handleize(value) {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function bundleComponent(productHandle, variantTitle) {
  return `${productHandle}-${handleize(variantTitle)}`;
}

if (!customElements.get('product-option-dropdowns')) {
  customElements.define(
    'product-option-dropdowns',
    class ProductOptionDropdowns extends HTMLElement {
      constructor() {
        super();

        this.form = document.getElementById(this.dataset.productFormId);
        this.selects = Array.from(this.querySelectorAll('select[data-metafield-key]'));
        this.awaitingCartUpdate = false;

        // One block per policy, not one per element: `data-trigger-keys` names
        // the dropdowns whose selection makes that block compulsory.
        this.acknowledgements = Array.from(
          this.querySelectorAll('.product-option-dropdowns__acknowledgement')
        ).map((wrapper) => {
          const triggerKeys = (wrapper.dataset.triggerKeys || '')
            .split(',')
            .map((key) => key.trim())
            .filter(Boolean);

          return {
            wrapper,
            input: wrapper.querySelector('.product-option-dropdowns__acknowledgement-input'),
            error: wrapper.querySelector('.product-option-dropdowns__acknowledgement-error'),
            selects: this.selects.filter((select) => triggerKeys.includes(select.dataset.metafieldKey)),
          };
        });

        this.selects.forEach((select) =>
          select.addEventListener('change', () => this.syncAcknowledgementVisibility())
        );

        this.acknowledgements.forEach((acknowledgement) => {
          acknowledgement.input?.addEventListener('change', () => {
            if (acknowledgement.input.checked) this.setAcknowledgementError(acknowledgement, false);
          });
        });

        if (this.form) {
          // Capture phase on the document, not on the form: at the target
          // itself listeners run in the order they were registered, and
          // product-form.js registers its submit handler first — from the
          // <product-form> constructor, before this element upgrades. Capturing
          // on an ancestor is the only way to stop the event before that
          // handler posts to /cart/add.js.
          this.onFormSubmitCapture = this.onFormSubmitCapture.bind(this);
          document.addEventListener('submit', this.onFormSubmitCapture, true);

          this.form.addEventListener('submit', () => {
            this.awaitingCartUpdate = true;
          });
        } else {
          console.warn(
            `product-option-dropdowns: no form found with id "${this.dataset.productFormId}" — addon items will not be added to cart.`
          );
        }

        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
      }

      connectedCallback() {
        this.syncAcknowledgementVisibility();

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.watchAvailability(), { once: true });
        } else {
          this.watchAvailability();
        }
      }

      disconnectedCallback() {
        if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
        this.availabilityObserver?.disconnect();
        if (this.onFormSubmitCapture) document.removeEventListener('submit', this.onFormSubmitCapture, true);
      }

      // An acknowledgement only applies to a garment that's being altered, so
      // each appears with the first selection among its own triggers rather
      // than sitting on the page by default.
      syncAcknowledgementVisibility() {
        this.acknowledgements.forEach((acknowledgement) => {
          const altered = acknowledgement.selects.some((select) => select.value);
          acknowledgement.wrapper.hidden = !altered;

          if (!altered) this.setAcknowledgementError(acknowledgement, false);
        });
      }

      // Compulsory while a checkbox is on screen. A hidden block means the
      // variant can't be bought anyway, and a hidden checkbox means nothing it
      // covers is being altered, so neither stands between the shopper and a
      // cart addition. A failed attempt flags every visible one at once so the
      // shopper isn't sent back twice.
      onFormSubmitCapture(event) {
        if (event.target !== this.form || this.hidden) return;

        const unticked = this.acknowledgements.filter(
          (acknowledgement) => !acknowledgement.wrapper.hidden && acknowledgement.input && !acknowledgement.input.checked
        );

        if (unticked.length === 0) return;

        event.preventDefault();
        event.stopPropagation();

        unticked.forEach((acknowledgement) => this.setAcknowledgementError(acknowledgement, true));
        unticked[0].input.focus();
      }

      setAcknowledgementError(acknowledgement, show) {
        acknowledgement.input?.setAttribute('aria-invalid', show ? 'true' : 'false');
        if (acknowledgement.error) acknowledgement.error.hidden = !show;
      }

      // Hemming / Fly / Collar are addons to a garment that can actually be
      // bought, so they follow the main submit button: product-info.js disables
      // it both for a sold-out variant and for an option combination that
      // doesn't exist. The button — not the variantChange payload — is the
      // signal, because no variantChange is published for the latter case
      // (product-info.js `setUnavailable()` returns before publishing).
      watchAvailability() {
        const submitButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);

        if (!submitButton) {
          console.warn(
            `product-option-dropdowns: no submit button found for section "${this.dataset.sectionId}" — options will stay visible on sold-out variants.`
          );
          return;
        }

        const sync = () => {
          // `data-awaiting-selection` means it is disabled only for want of a
          // size. The garment is still buyable, so the addons stay on screen.
          this.hidden =
            submitButton.hasAttribute('disabled') && !submitButton.hasAttribute('data-awaiting-selection');
        };

        this.availabilityObserver = new MutationObserver(sync);
        this.availabilityObserver.observe(submitButton, { attributes: true, attributeFilter: ['disabled'] });
        sync();
      }

      onCartUpdate(event) {
        if (event.source !== 'product-form' || !this.awaitingCartUpdate) return;

        this.awaitingCartUpdate = false;

        const mainLine = event.cartData;
        const addons = this.selects
          .map((select) => {
            if (!select.value) return null;

            return {
              label: select.dataset.label,
              id: select.value,
              quantity: mainLine.quantity || 1,
              productHandle: select.dataset.productHandle,
              variantTitle: select.selectedOptions[0] ? select.selectedOptions[0].textContent.trim() : '',
            };
          })
          .filter(Boolean);

        if (addons.length === 0) return;

        // The bundle id is derived from every member's product handle + variant
        // title, not from any single line's key — line keys are re-hashed by
        // Shopify whenever a line's properties change, so a key-based reference
        // (the previous approach) can go stale. This string stays stable for the
        // life of the bundle.
        const bundleId = [
          bundleComponent(mainLine.handle, mainLine.variant_title),
          ...addons.map((addon) => bundleComponent(addon.productHandle, addon.variantTitle)),
        ].join('--');

        // Tag the main line first so the addon adds below always land in a cart
        // that already reflects the bundle id — sequential, not concurrent, to
        // avoid racing two /cart/change.js-style mutations against the same cart.
        this.tagMainLineWithBundleId(mainLine, bundleId).then(() => {
          // Added as separate requests, one per addon — not batched into a single
          // `items` array — because /cart/add.js treats a multi-item request as
          // atomic: one invalid item (e.g. not published to Online Store, out of
          // stock) fails the whole call and silently drops every addon, not just
          // the invalid one.
          addons.forEach((addon) =>
            this.addAddonItem({
              label: addon.label,
              id: addon.id,
              quantity: addon.quantity,
              properties: {
                _bundle_id: bundleId,
                _bundle_addon: 'true',
                // Hidden (underscore-prefixed) so Shopify's checkout doesn't
                // surface it — cart-item-linked-properties.liquid reads these
                // two directly to render the option nested under the main line.
                _option_label: addon.label,
                _option_value: addon.variantTitle,
                // The only visible property on this addon's own line, so
                // checkout shows which garment it belongs to instead of
                // repeating the addon's own title/variant back at itself.
                [mainLine.product_title]: mainLine.variant_title,
              },
            })
          );
        });
      }

      tagMainLineWithBundleId(mainLine, bundleId) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = {
          id: mainLine.key,
          quantity: mainLine.quantity,
          properties: { ...mainLine.properties, _bundle_id: bundleId },
        };

        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        return fetch(`${routes.cart_change_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.errors) {
              console.error('product-option-dropdowns: failed to tag main item with bundle id', response.errors);
              return;
            }

            if (cart) cart.renderContents(response);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-option-dropdowns', cartData: response });
          })
          .catch((e) => console.error('product-option-dropdowns: request to tag main item with bundle id failed', e));
      }

      addAddonItem(item) {
        const { label, ...apiItem } = item;
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items: [apiItem] };

        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error(`product-option-dropdowns: failed to add "${label}" item to cart`, response);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-option-dropdowns',
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }

            if (cart) cart.renderContents(response);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-option-dropdowns',
              cartData: response,
            });
          })
          .catch((e) => console.error('product-option-dropdowns: request to add addon item failed', e));
      }
    }
  );
}
